import { Currencies } from "@/constants/currency";
import {
  IVatToPrint,
  IVatToPrintNum,
  VatLineType,
} from "@/interfaces/receipts";
import BookingModule from "@/store/modules/booking";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import {
  AccountingState,
  IAccounting,
  PaymentMethods,
  TransactionTypes,
} from "@/types/db/accounting";
import {
  BookingState,
  IBookingCustomerInfo,
  IBookingTicketSearch,
  IProductSlotBooking,
  BaseOrderSession,
  BookingType,
} from "@/types/db/product-slot-booking";
import {
  BookingStatus,
  IProductSlotState,
  isSeatedPssType,
  PaymentStatus,
  PSSType,
  ScanningStatus,
} from "@/types/db/product-slot-state";
import dayjs from "dayjs";
import randomstring from "randomstring";
import { SharedCallbacks } from "../shared-resources";
import {
  excludedTaxByPssInBooking,
  excludedTaxByPssInOrder,
  restToPay,
  totalDiscountsByPss,
  totalDiscountsByPssInOrder,
} from "../vue-helpers/new-accountings";
import { changeSeatsStatuses } from "../vue-helpers/seats-io";
import { createCompositeId, generateID, generateUuidv4 } from "./app";
import { sumArray } from "./common";
import { nowDB } from "./dates";
import { getLanguage, getLanguageId } from "./lang";
import { getPSSValue } from "./product-slot-state";
import { OrderSessionState } from "@smeetz/order-common";
import { bookingCurrencyToOrderCurrency } from "./order-session";
import AuthModule from "@/store/modules/auth";
import OffersModule from "@/store/modules/offers";
import { beVatRates, frVatRates, hrVatRates } from "./compliance";
import { BookingStep } from "@/constants/booking";
import { createTicketAccountings } from "../vue-helpers/accounting";
import { esVatRates } from "@/utils/helpers/compliance";
import {
  isBelgianCompliant,
  isCroatianCompliant,
  isFrenchCompliant,
  isSpanishCompliant,
} from "../vue-helpers/compliance";

export function createBooking(): IProductSlotBooking {
  const creationTime = dayjs().toISOString();
  const uuid = generateID();
  const reference = randomstring.generate({
    length: 10,
    charset: "alphanumeric",
    capitalization: "lowercase",
  });
  const getSellerId = SharedCallbacks.getSellerId;

  if (!getSellerId) {
    throw new Error("No get seller id callback defined");
  }

  const cashfloatCallback = SharedCallbacks.cashfloat;

  if (!cashfloatCallback) {
    throw new Error("No cashfloat callback defined");
  }
  const cashfloat = cashfloatCallback();
  if (!cashfloat) {
    throw new Error("No cashflloat in app");
  }

  const getPOSConfigId = SharedCallbacks.getPOSConfigId;
  if (!getPOSConfigId) {
    throw new Error("No pos config callback defined");
  }
  const posConfigId = getPOSConfigId();
  if (!posConfigId) {
    throw new Error("No pos config in app");
  }
  const sellerId = getSellerId();
  POSConfigModule.selectGrid(POSConfigModule.firstGrid);
  // get the group currency
  const currency = POSConfigModule.groupInfo?.currency as Currencies;
  const groupId = Number(POSConfigModule.groupInfo?.id);

  // initial order session
  const orderSession = createOrderSession({
    bookingUuid: uuid,
    currency,
    sellerId,
    sellerCogn: AuthModule.cognitoId,
  });

  const booking = {
    _id: createCompositeId([creationTime, uuid]),
    uuid,
    reference,
    state: BookingState.PendingInfo,
    // TODO @Riyadh change to browser langauge
    languageId: getLanguageId(getLanguage()),
    // TODO @Riyadh
    currency: Currencies[currency] || "CHF",
    sellerId,
    insertDateTime: creationTime,
    updatedAt: creationTime,
    message: "",
    pssList: [],
    accountings: [],
    accountingEntries: [],
    cashfloatUuid: cashfloat.uid,
    firstPosConfigId: posConfigId,
    groupId: groupId,
    orderSessions: [orderSession],
    step: BookingStep.Order,
  };

  return booking;
}

/**
 * Returns the number of occurences of tickets identified by criteria in a give booking
 * criteria.timeSlotId is optional for merch products
 * @param booking booking to search
 * @param criteria criteria to search for tickets
 * @returns
 */
export function priceCountInBooking(
  booking: IProductSlotBooking,
  criteria: IBookingTicketSearch
) {
  const { priceId, categoryId, timeslotId } = criteria;

  const pssList = booking.pssList || [];

  let count = 0;
  for (const pss of pssList) {
    // lets ignore the pss if its an addon
    if (pss.mainTicketUUID) continue;
    const {
      priceId: pssPriceId,
      categoryId: pssCategoryId,
      timeSlotId: pssTimeslotId,
      pssType: pssType,
    } = pss;
    const timeslotMatch =
      pssPriceId === priceId &&
      pssCategoryId === categoryId &&
      pssTimeslotId === timeslotId &&
      pssType === PSSType[PSSType.Ticket];
    const retailMatch =
      pssPriceId === priceId &&
      pssCategoryId === categoryId &&
      pssType === PSSType[PSSType.Retail];
    if (
      pss.bookingStatus !== BookingStatus.Abandoned &&
      pss.bookingStatus !== BookingStatus.Canceled &&
      (timeslotMatch || retailMatch)
    ) {
      count++;
    }
  }

  return count;
}

export function bookingTotal(booking: IProductSlotBooking) {
  let amount = 0;
  const pssList = booking.pssList;

  if (!pssList) {
    return amount;
  }

  for (let i = 0; i < pssList.length; i++) {
    amount += getPSSValue(pssList[i]) / 100;
  }

  return amount;
}

/**
 * Takes a booking and return an array of Vats to print
 * @param booking
 */
export function getVats(
  booking: IProductSlotBooking,
  options: {
    forBill?: boolean;
    /**
     * Narrows the VAT lines to one order session. When set, discounts and
     * excluded tax are resolved against this subset rather than the whole
     * booking — used by the Belgian receipt, which covers a single sale.
     */
    pssList?: IProductSlotState[];
    accountings?: IAccounting[];
  } = {}
): IVatToPrint[] {
  const vatsToPrint: IVatToPrint[] = [];
  const vatsToPrintNum: IVatToPrintNum[] = [];
  const isScopedToOrderSession = !!options.pssList;
  const pssList = options.pssList ?? booking.pssList;
  const accountings = options.accountings ?? booking.accountings;
  if (!pssList) {
    return [];
  }

  const ticketAndDiscountAccEntries =
    accountings
      .filter((acc) => {
        const isCompleted = acc.state === AccountingState.Completed;
        const isTicket =
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TICKET;
        const isDiscount =
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT;

        if (isDiscount && options.forBill) {
          return acc; // we want to include discounts for bill printing
        }

        if (!isCompleted) return false;

        return (isTicket && !acc.refund) || isDiscount;
      })
      .flatMap((acc) => acc.accountingEntriesList) || [];

  if (options.forBill) {
    const unconfirmedTicketAccountings = createTicketAccountings(booking);
    ticketAndDiscountAccEntries.push(
      ...unconfirmedTicketAccountings.flatMap(
        (acc) => acc.accountingEntriesList
      )
    );
  }
  let vatIndex = 1;
  // const pssFinalValues = BookingModule.pssFinaLValuesAfterDiscount;
  for (const pss of pssList) {
    if (!pss || !pss.uuid) continue;
    if (pss.bookingStatus === BookingStatus.Abandoned) continue;

    // let's get the pss total tax value
    const correspondingAccEntries = ticketAndDiscountAccEntries.filter(
      (ae) => ae.productSlotStateUuid === pss.uuid
    );
    if (!correspondingAccEntries.length) continue;
    const pssTotalTax = Math.abs(
      sumArray(
        correspondingAccEntries.map((ae) => ae.salesTaxMicroValue || 0)
      ) / 10000
    );
    const vatAmount = Math.abs(pssTotalTax);

    const ttlDiscountsOfPss = isScopedToOrderSession
      ? totalDiscountsByPssInOrder(String(pss.uuid), pssList, accountings)
      : totalDiscountsByPss.value(String(pss.uuid));

    const excludedTax = isScopedToOrderSession
      ? excludedTaxByPssInOrder(String(pss.uuid), pssList, accountings)
      : excludedTaxByPssInBooking.value(pss.uuid, booking);

    // A cancelled ticket reverses its own discount and excluded tax, so its VAT
    // line mirrors the sale it is undoing.
    const pssFinalValue =
      pss.bookingStatus === BookingStatus.Canceled && isScopedToOrderSession
        ? getPSSValue(pss) + ttlDiscountsOfPss - excludedTax
        : getPSSValue(pss) - ttlDiscountsOfPss + excludedTax;
    const vatRateInt = (correspondingAccEntries[0].salesTaxRate || 0) / 10000;
    const vatRate = vatRateInt.toFixed(2) + "%";
    const amountExclVat = pssFinalValue ? pssFinalValue - pssTotalTax : 0;
    const amountInclVat = pssFinalValue;

    const groupedPrice = vatsToPrintNum.find(
      (p) =>
        p.priceId.includes(pss.priceId) &&
        p.rate === vatRate &&
        p.type === bookingStatusToVatType(pss)
    );

    if (!groupedPrice) {
      // we dont have this Tax for this price yet in the array
      vatsToPrintNum.push({
        name: "",
        rate: vatRate,
        vatAmount: vatAmount,
        amountExclVat: amountExclVat,
        amountInclVat: amountInclVat,
        priceId: [pss.priceId],
        vatIndex: `(${
          (isSpanishCompliant.value && (esVatRates[vatRate] || "X")) ||
          (isFrenchCompliant.value && frVatRates[vatRate]) ||
          (isCroatianCompliant.value && hrVatRates[vatRate]) ||
          (isBelgianCompliant.value && (beVatRates[vatRate] || "X")) ||
          vatIndex
        })`,
        type: bookingStatusToVatType(pss),
      });
      vatIndex++;
    } else {
      // we already have this tax for this price
      groupedPrice.vatAmount += vatAmount;
      groupedPrice.amountExclVat += amountExclVat;
      groupedPrice.amountInclVat += amountInclVat;
    }
  }

  // convert to major currency unit and round to 2 decimals
  for (const vat of vatsToPrintNum) {
    vatsToPrint.push({
      name: vat.name,
      rate: vat.rate,
      vatAmount: (vat.vatAmount / 100).toFixed(2),
      amountExclVat: (vat.amountExclVat / 100).toFixed(2),
      amountInclVat: (vat.amountInclVat / 100).toFixed(2),
      priceId: vat.priceId,
      vatIndex: vat.vatIndex,
      type: vat.type,
    });
  }
  return vatsToPrint;
}

function bookingStatusToVatType(pss: IProductSlotState) {
  return pss.bookingStatus === BookingStatus.Canceled
    ? VatLineType.Cancellation
    : VatLineType.Purchase;
}

/**
 * Sets booking and all the pss of a booking to completed
 * Auto scan tickets if it is set to
 */
export async function completeBooking(booking: IProductSlotBooking) {
  console.log("+++++++++++++++++ hi from completeBooking");
  const now = nowDB();
  booking.state = BookingState.Completed;
  booking.updatedAt = now;
  const pssList = booking.pssList;
  const autoScan = POSConfigModule.getAutoScanTickets;

  // If a pss is cancelled we don't complete it
  // If a pss has some amount paid we complete its bookingStatus
  // If a pss is fully paid we complete its paymentStatus
  const pssListBalances = BookingModule.pssListBalances;
  const orderRestToPay = restToPay.value;
  // const confirmedSeatsPSS: IProductSlotState[] = [];
  for (const pss of pssList) {
    // console.log("+++++++++++++++++ inside for of loop");
    if (
      pss.bookingStatus === BookingStatus.Canceled ||
      pss.bookingStatus === BookingStatus.Abandoned
    ) {
      // cancelled/abandoned pss are not to be confirmed
      continue;
    }

    // if pss has membership voucher applied, we update the pss.token
    if (pss.appliedMembershipToken) {
      pss.token = pss.appliedMembershipToken;
    }

    // if pss has a payment we confirmed we confirm its booking status
    const balance = pssListBalances.find((b) => b.pssUuid === pss.uuid);
    if (!balance) {
      continue;
    } else {
      if (balance.accEntries && balance.accEntries.length) {
        // so this pss has payment confirmed
        pss.bookingStatus = BookingStatus.Confirmed;
        console.log("------------ pss bookingStatus confirmed 1");
        // // Now lets check if this pss is actually a seating plan ticket, so that we Book it in seats io level
        // // we will do so by calling an endpoint to change the status from `hold`to `booked`
        // if (pss.type === PCTypes.SeatedSub) {
        //   // So this pss is of type SeatedSub
        //   // lets now check if this pss has fullSeat info
        //   if (pss.fullSeat) {
        //     // This pss has fullSeat info which is actually coming from seats io when we confirmed the seat (seat status from `hold`to `booked')
        //     // it means there is something wrong here
        //     LogService.info(
        //       `This pss had already fullSeat data before getting confirmed in seats io ${pss.uuid}`,
        //       { params: pss },
        //       LogLevel.Error
        //     );
        //   } else {
        //     // so lets confirm this pss seat in seats io level
        //     // and then assign pss.fullSeat a value
        //     if (pss.seat && pss.eventKey) {
        //       confirmedSeatsPSS.push(pss);
        //     }
        //   }
        // }

        // if pss is actually fully paid then we confirm its paymentStatus
        // pss is considered paid if the whole order is fully paid even if this pss has no accEntry. Example:
        // user cancelled a paid pss and book same value pss (L3-3072)
        if (balance.balance === 0 || orderRestToPay === 0) {
          // so this pss is fully paid, lets confirm its paymentStatus
          // Keep in mind that an order that is an invoice type can have their payment confirmed only from BO, ie, pss booking status are confirmed but paymentStatus should not be completed
          // Lets skip Invoice type bookings from completing their paymentStatus
          if (booking.type !== BookingType.Invoice) {
            pss.paymentStatus = PaymentStatus.Completed;
          }

          if (
            autoScan &&
            pss.pssType !== PSSType[PSSType.Retail] &&
            pss.scanningStatus !== ScanningStatus.Scanned
          ) {
            // lets auto scan the pss
            pss.scanningStatus = ScanningStatus.Scanned;
            pss.scanningUserId = UserModule.userId;
            // scannedSeatsPSS.push(pss);
          }
        }
      }
    }
    // pss.bookingStatus = BookingStatus.Confirmed;
    // pss.paymentStatus = PaymentStatus.Completed;
    pss.updatedAt = nowDB();
  }

  // User now has confirmed the booking, and the booking is going to be synchyed
  // lets check if this booking has seating plan PSS so that we change their status at seats io level
  // seats status can be:
  // `scanned`if pss.scannedStatus is confirmed
  // `paid`if pss.paymentStatus is confirmed
  // `booked` if pss.bookingStatus is confirmed
  if (booking) {
    const pssList = booking.pssList;
    if (pssList && pssList.length) {
      // We take into consideration new seating PSS that was created now in frontend
      // The ones that are cancelled we ignore them, because their seats have been freed in the moment the user removed the PSS on UI
      let newSeatingPSSList = pssList.filter(
        (ps) =>
          isSeatedPssType(ps.type) &&
          !ps.id &&
          ps.bookingStatus !== BookingStatus.Canceled &&
          ps.bookingStatus !== BookingStatus.Abandoned
      );

      // Lets filter out pss that already has fullSeat object
      newSeatingPSSList = newSeatingPSSList.filter((p) => !p.fullSeat);
      console.log("newSeatingPSSList");
      console.log(newSeatingPSSList);
      if (newSeatingPSSList && newSeatingPSSList.length) {
        console.log("------ L3-6757 JSON.stringify(newSeatingPSSList)");
        console.log(JSON.stringify(newSeatingPSSList));
        console.log(
          "------ L3-6757 calling changeSeatsStatuses() from utils/helpers/completeBooking"
        );
        await changeSeatsStatuses(newSeatingPSSList, booking);
      } else {
        console.log(
          "----- L3-6757 POS tried to call change-booking-status of pss that already had pss.fullSeat"
        );
      }
    }

    // Lets check if this booking has offers applied with confirmed states
    const confirmedOffersAccountings =
      booking.accountings.filter(
        (acc) =>
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
          acc.state === AccountingState.Completed &&
          (acc.paymentMethod === PaymentMethods.METHOD_OFFER ||
            acc.paymentMethod === PaymentMethods.METHOD_POS_OFFER) &&
          !acc.refund
      ) || [];
    if (confirmedOffersAccountings.length) {
      const confirmedOffersUuids = confirmedOffersAccountings.map((acc) =>
        String(acc.uuid)
      );
      OffersModule.confirmAppliedOffers({
        bookingUuid: String(booking.uuid),
        offerApplicationsUuids: confirmedOffersUuids,
      });
    }
  }
}

/**
 * Sets customer info to a booking right before inserting it into the db
 */
export function setCustomerInfo(booking: IProductSlotBooking) {
  const idCallback = SharedCallbacks.getSelectedCustomerId;
  if (!idCallback) {
    throw new Error("No customer id callback defined");
  }
  const id = idCallback();

  const emailCallback = SharedCallbacks.getSelectedCustomerEmail;
  if (!emailCallback) {
    throw new Error("No customer email callback defined");
  }
  const email = emailCallback();

  const firstNameCallback = SharedCallbacks.getSelectedCustomerFirstName;
  if (!firstNameCallback) {
    throw new Error("No customer firstname callback defined");
  }
  const firstname = firstNameCallback();

  const lastNameCallback = SharedCallbacks.getSelectedCustomerLastName;
  if (!lastNameCallback) {
    throw new Error("No customer lastname callback defined");
  }
  const lastname = lastNameCallback();

  const data: IBookingCustomerInfo = {
    id,
    bookingUuid: String(booking.uuid),
    email: email,
    lastName: lastname,
    firstName: firstname,
  };
  const setCustomerInfo = SharedCallbacks.setCustomerInfo;
  if (setCustomerInfo) {
    setCustomerInfo(data);
  }
}

export function createOrderSession(data: {
  bookingUuid: string;
  currency: Currencies;
  sellerId: number;
  sellerCogn: string;
}) {
  const { bookingUuid, currency, sellerId, sellerCogn } = data;
  const orderId = generateUuidv4();
  const orderRef = randomstring.generate({
    length: 10,
    charset: "alphanumeric",
    capitalization: "uppercase",
  });
  const groupId = Number(POSConfigModule.groupInfo?.id);
  const creationTime = dayjs().toISOString();
  // initial order session
  const orderSession: BaseOrderSession = {
    id: orderId, //uuid
    orderId: orderId,
    reference: orderRef,
    bookingUuid: bookingUuid,
    currency: bookingCurrencyToOrderCurrency(currency),
    state: OrderSessionState.PENDING,
    groupId: groupId,
    sellerId,
    sellerCogn,
    channel: "Smeetz POS",
    sessionStart: creationTime,
    createdAt: creationTime,
    updatedAt: creationTime,
  };
  return orderSession;
}
